import { decodeIdBase64 } from '@/shared/helpers/utils';
import { getCodeById } from '@/shared/helpers/api';
import { http } from '@/shared/httpWrapper.js';
import { useCableEditStore } from '@/stores/cableEditStore.ts';
import { useSchemaEditStore } from '@/stores/schemaEditStore.ts';
import type { ElectricalComponentStore } from '@/stores/electricalComponentStore.ts';
import type { PreselectPipeClassStore } from '@/stores/preselectPipeClassStore.ts';

export type SpecializedUiLibrary = {
    name: string;
};

export type SpecializedUiReferenceCode = {
    id?: string | number;
    identity?: string;
    description?: string;
    name?: string;
};

export type SpecializedUiCodeAttribute = {
    definitionName: string;
    displayValue?: string;
    referenceCode?: SpecializedUiReferenceCode;
    identity?: string;
    id?: string;
};

export type SpecializedUiCode = {
    id?: string;
    name?: string;
    description?: string;
    identity?: string;
    iri?: string;
    dateUpdated?: string;
    dateCreated?: string;
    isValid?: boolean;
    attributes?: SpecializedUiCodeAttribute[];
};

export type SpecializedUiCodeSet = {
    scopes: string[];
};

type RouteWithParams = {
    name: string | undefined;
    params: {
        identity?: string | null;
        tab?: string;
        selectedTab?: string;
    };
    query?: Record<string, string | null | undefined> | null;
};

type RouteWithOptionalName = {
    name?: string;
};

export function getCodeAttribute(code: SpecializedUiCode | null = null, attributeDefinitionName: string): SpecializedUiCodeAttribute | undefined {
    return code
        ?.attributes
        ?.find(attribute => attribute.definitionName === attributeDefinitionName);
}

export function getCodeAttributeValue(code: SpecializedUiCode | null = null, attributeDefinitionName: string): string | undefined {
    return getCodeAttribute(code, attributeDefinitionName)
        ?.displayValue;
}

export function getCodeAttributeIdentity(code: SpecializedUiCode | null = null, attributeDefinitionName: string): string | undefined {
    return getCodeAttribute(code, attributeDefinitionName)
        ?.referenceCode
        ?.identity;
}

function getCodeAttributeReferenceId(code: SpecializedUiCode, attributeDefinitionName: string): number {
    const idValue = getCodeAttribute(code, attributeDefinitionName)
        ?.referenceCode
        ?.id;

    const value = decodeIdBase64(String(idValue));
    return parseInt(value, 10);
}

function getCodeId(code: SpecializedUiCode): number {
    return parseInt(decodeIdBase64(String(code.id)), 10);
}

export function getRoute(routeName: string): { name: string } {
    return {
        name: routeName
    };
}

export const schemaRouteDictionary: Record<string, string> = {
    'SchemaMapClass': 'SchemaMapClass',
    'SchemaMapAttribute': 'SchemaMapAttribute',
    'SchemaMapRelation': 'SchemaMapRelation',
    'SchemaAttribute': 'SchemaAttribute',
    'SchemaClass': 'SchemaClass',
    'SchemaClassInterface': 'SchemaClass',
    'SchemaRelation': 'SchemaRelation',
    'SchemaInterface': 'SchemaInterfaceEditor'
};

export function getSchemaRoute(library: SpecializedUiLibrary, code: SpecializedUiCode | null = null, schema: { Identity: string } | null = null): RouteWithParams {
    const attributeDefinitionName = 'Schema';

    const route: RouteWithParams = {
        name: schemaRouteDictionary[library.name],
        params: {
            identity: null
        }
    };

    if (schema) {
        route.params.identity = schema.Identity;
    }

    if (code !== null && code !== undefined) {
        route.params.identity = getCodeAttributeValue(code, attributeDefinitionName);

        const store = useSchemaEditStore();

        if (library.name === 'SchemaAttribute') {
            const attributeId = getCodeId(code);
            const interfaceId = getCodeAttributeReferenceId(code, 'Interface');

            store.setAttributeId(attributeId);
            store.setInterfaceId(interfaceId);
        } else if (library.name === 'SchemaClass') {
            const codeId = getCodeId(code);
            store.setClassId(codeId);
        } else if (library.name === 'SchemaClassInterface') {
            const classId = getCodeAttributeReferenceId(code, 'Class');
            const interfaceId = code.identity;

            store.setClassId(classId);
            if (interfaceId !== undefined) {
                store.setClassInterfaceId(interfaceId);
            }
        } else if (library.name === 'SchemaInterface') {
            const interfaceId = getCodeId(code);
            store.setInterfaceId(interfaceId);
        } else if (library.name.startsWith('SchemaMap')) {
            const schemaSource = getCodeAttributeValue(code, 'Schema1');
            const schemaTarget = getCodeAttributeReferenceId(code, 'Schema2');

            route.params.identity = schemaSource;

            if (library.name === 'SchemaMapClass') {
                const selectedClassOne = getCodeAttributeReferenceId(code, 'Class1');
                const selectedClassTwo = getCodeAttributeReferenceId(code, 'Class2');

                store.setMapSchemaId(schemaTarget);
                store.setMapSchemaObject({
                    class1: selectedClassOne,
                    class2: selectedClassTwo
                });
            }

            if (library.name === 'SchemaMapAttribute') {
                const selectedAttributeOne = getCodeAttributeReferenceId(code, 'Attribute1');
                const selectedAttributeTwo = getCodeAttributeReferenceId(code, 'Attribute2');

                store.setMapSchemaId(schemaTarget);
                store.setMapSchemaObject({
                    attribute1: selectedAttributeOne,
                    attribute2: selectedAttributeTwo
                });
            }

            if (library.name === 'SchemaMapRelation') {
                const schemaTarget = getCodeAttributeReferenceId(code, 'Schema2');
                const selectedRelation = getCodeAttributeReferenceId(code, 'Relation1');

                store.setMapSchemaId(schemaTarget);
                store.setRelationId(selectedRelation);
            }
        } else if (library.name === 'SchemaRelation') {
            const relationId = getCodeId(code);
            const interfaceId = getCodeAttributeReferenceId(code, 'LeftInterface');

            store.setRelationId(relationId);
            store.setInterfaceId(interfaceId);
        }
    }

    return route;
}

export function getEnsFormatRoute(specializedUI: string, library: SpecializedUiLibrary, codeSet: SpecializedUiCodeSet, code: SpecializedUiCode | null = null): { name: string; query: Record<string, string | null | undefined> } {
    const route: { name: string; query: Record<string, string | null | undefined> } = {
        name: specializedUI,
        query: {
            facility: codeSet.scopes[0],
            formatIdentity: null
        }
    };

    if (library.name === 'DocumentNumberingFormatElement') {
        route.query.formatIdentity = getCodeAttributeIdentity(code, 'DocNoFormat');
    } else if (library.name === 'TagFormatElement') {
        route.query.formatIdentity = getCodeAttributeIdentity(code, 'TagFormat');
    } else {
        route.query.formatIdentity = code?.identity;
    }

    return route;
}

export function getLCIRoute(tab: string, codeSet: SpecializedUiCodeSet, code: SpecializedUiCode | null = null): RouteWithParams {
    const route: RouteWithParams = {
        name: 'LCITab',
        params: {
            tab: tab
        },
        query: null
    };

    if (tab === 'Projects') {
        route.query = {
            facility: getCodeAttributeValue(code, 'Facility'),
            project: encodeURI(String(code?.name))
        };
    } else if (tab === 'Contractors') {
        route.query = {
            facility: codeSet.scopes[0],
            contractor: code?.name
        };
    } else if (tab === 'PurchaseOrders') {
        route.query = {
            facility: codeSet.scopes[0],
            purchaseOrder: code?.name
        };
    }
    return route;
}

export function getFacilityAndProjectRoute(tab: string, code: SpecializedUiCode | null = null): RouteWithOptionalName {
    const route: RouteWithOptionalName = {};

    switch (tab) {
        case 'Contract':
            route.name = code ? 'EditContract' : 'CreateNewContract';
            break;
        case 'ProjectMaster':
            route.name = code ? 'FacilityAndProjectById' : 'CreateNewProjectMaster';
            break;
        case 'Project':
            route.name = code ? 'EditProject' : 'CreateNewProject';
            break;
    }

    return route;
}

const cableEditTabs: Record<string, string> = {
    'CableCode': 'Code',
    'CableSpec': 'Specification',
    'CableCodeSheath': 'SheathColour',
    'CableCodeType': 'Type',
    'CableSpecWire': 'Specification'
};

export async function getCableEditRoute(library: SpecializedUiLibrary, code: SpecializedUiCode | null = null): Promise<RouteWithParams> {
    const tab = cableEditTabs[library.name];
    const cableEditStore = useCableEditStore();

    if (code !== null && code !== undefined) {
        if (library.name === 'CableCode') {
            if (code.identity !== undefined) {
                cableEditStore.setCableCode(code.identity);
            }
        } else {
            const codeIdentity = getCodeAttributeIdentity(code, 'CableCode');
            if (codeIdentity !== undefined) {
                cableEditStore.setCableCode(codeIdentity);
            }
        }

        if (library.name === 'CableSpec') {
            if (code.identity !== undefined) {
                cableEditStore.setCableSpec(code.identity);
            }
        }

        if (library.name === 'CableSpecWire') {
            const specIdentity = getCodeAttributeIdentity(code, 'CableSpec');

            const referenceId = getCodeAttributeReferenceId(code, 'CableSpec');
            const referenceCode = await getCodeById(http, referenceId);
            const codeIdentity = getCodeAttributeValue(referenceCode, 'CableCode');

            if (codeIdentity !== undefined) {
                cableEditStore.setCableCode(codeIdentity);
            }
            if (specIdentity !== undefined) {
                cableEditStore.setCableSpec(specIdentity);
            }
        }
    }

    return {
        name: 'CableEditTab',
        params: {
            selectedTab: tab
        }
    };
}

const pipingClassEditorRouteDictionary: Record<string, string> = {
    'PipingClass': 'Class',
    'PipingClassSize': 'ClassSize',
    'LineSize': 'LineSize'
};

export function getPipeClassEditorRoute(pipeClassStore: PreselectPipeClassStore, library: SpecializedUiLibrary, _codeSet: SpecializedUiCodeSet, code: SpecializedUiCode | null = null): RouteWithParams {
    const tab = pipingClassEditorRouteDictionary[library.name];

    if (code !== null && code !== undefined) {
        const codeId = getCodeId(code);

        if (tab === 'Class') {
            pipeClassStore.setPipingClass(codeId);
        } else if (tab === 'ClassSize') {
            const classRefCode = getCodeAttribute(code, 'PipingClass')?.referenceCode?.id;

            const pipingClassId = parseInt(decodeIdBase64(String(classRefCode)));

            pipeClassStore.setPipingClass(pipingClassId);
            pipeClassStore.setPipingClassSize(codeId);
        } else {
            pipeClassStore.setLineSize(codeId);
        }
    }

    return {
        name: 'PipeClassEditor',
        params: {
            selectedTab: tab
        }
    };
}

export function getElectricComponentRoute(electricalComponentStore: ElectricalComponentStore, _codeSet: SpecializedUiCodeSet, code: SpecializedUiCode | null = null): { name: string } {
    const route = {
        name: 'ElectricComponentType'
    };

    if (code !== null && code !== undefined) {
        const electricalComponentId = getCodeId(code);
        electricalComponentStore.setElectricalComponent(electricalComponentId);
    }

    return route;
}
